import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { coverDocStoreWarning } from 'fym-shared/src/music24/coverDoc';
import { TypedMemo } from '../../common/types';
import { useCoverEditor } from '../CoverEditorContext';
import { coverEditorMessages } from '../coverEditorMessages';
import { publicCoverMessages } from './publicCoverMessages';
import type { PublicCoverNote } from './usePublicCoverSource';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import editorStyles from '../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PublicNoticesProps {
  note: PublicCoverNote;
  onDismiss: () => void;
  /** The file could not be made in this browser. */
  isFileFailed: boolean;
  /** The two fields: the store note compares the text boxes with them (owner decision D9). */
  title: string;
  artist: string;
  /** The text of the boxes while a field is empty: it is not "other text". */
  placeholders: { title: string; artist: string };
}

/**
 * The short notes over the stage of the public tool: a design link that does not work, an own
 * picture that this browser did not keep, a file that could not be made, and the store note.
 */
const PublicNoticesComponent: React.FC<PublicNoticesProps> = (props) => {
  const { note, onDismiss, isFileFailed, title, artist, placeholders } = props;
  const intl = useIntl();
  const { state } = useCoverEditor();
  const hasStoreWarning = useMemo(
    () =>
      coverDocStoreWarning(
        // A box that still shows the placeholder of an empty field is the title or the artist to come.
        { texts: state.doc.texts.filter((box) => box.text !== placeholders.title && box.text !== placeholders.artist) },
        { title, artist }
      ),
    [artist, placeholders.artist, placeholders.title, state.doc.texts, title]
  );
  if (note === null && !isFileFailed && !hasStoreWarning) {
    return null;
  }
  return (
    <div className={editorStyles.notices}>
      {note !== null && (
        <p className={editorStyles.notice} role="status">
          {intl.formatMessage(note === 'uploadLost' ? publicCoverMessages.uploadLost : publicCoverMessages.loadFailed)}
          <button type="button" className={editorStyles.linkButton} onClick={onDismiss}>
            {intl.formatMessage(publicCoverMessages.dismiss)}
          </button>
        </p>
      )}
      {isFileFailed && (
        <p className={`${editorStyles.notice} ${editorStyles.noticeWarn}`} role="alert">
          {intl.formatMessage(publicCoverMessages.downloadFailed)}
        </p>
      )}
      {hasStoreWarning && (
        <p className={`${editorStyles.notice} ${editorStyles.noticeWarn}`}>
          {intl.formatMessage(coverEditorMessages.storeNote)}
        </p>
      )}
    </div>
  );
};

export const PublicNotices = TypedMemo(PublicNoticesComponent);
